import {
  MediaDeleteModal,
  MediaDeleteModalRef,
  mediaToDeleteItems,
} from '@components/MediaDeleteModal/MediaDeleteModal';
import { useLibrary } from '@stores/Library/Library';
import { useLibraryFolder } from '@stores/Library/LibraryFolder';
import { useUploadManager } from '@stores/UploadManager/UploadManager';
import { Media } from '@typings/Media';
import {
  createContext,
  MutableRefObject,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export type OpenDeleteModalFn = (media: Media | Media[], onDeleted?: () => void) => void;

interface MediaDeleteModalContextType {
  openDeleteModal: OpenDeleteModalFn;
}

const MediaDeleteModalContext = createContext<MediaDeleteModalContextType | null>(null);

export const useMediaDeleteModal = () => {
  const context = useContext(MediaDeleteModalContext);
  if (!context) {
    throw new Error('useMediaDeleteModal must be used within a MediaDeleteModalProvider');
  }
  return context;
};

interface MediaDeleteModalProviderProps {
  openRef: MutableRefObject<OpenDeleteModalFn | null>;
  children: ReactNode;
}

/**
 * Provides openDeleteModal that delegates to openRef.current.
 * Does NOT hold modal state, so opening the modal does not re-render children (e.g. the table).
 */
export const MediaDeleteModalProvider = ({ openRef, children }: MediaDeleteModalProviderProps) => {
  const openDeleteModal = useCallback<OpenDeleteModalFn>(
    (media: Media | Media[], onDeleted?: () => void) => {
      openRef.current?.(media, onDeleted);
    },
    [openRef],
  );

  const contextValue = useMemo(() => ({ openDeleteModal }), [openDeleteModal]);

  return (
    <MediaDeleteModalContext.Provider value={contextValue}>
      {children}
    </MediaDeleteModalContext.Provider>
  );
};

interface MediaDeleteModalHostProps {
  openRef: MutableRefObject<OpenDeleteModalFn | null>;
}

/**
 * Holds modal state and renders the modal. Rendered as a SIBLING of the provider's children
 * so that when openDeleteModal is called, only this host re-renders, not the table.
 *
 * Host owns the folder/library hooks and passes them as props to the modal — the modal
 * itself is context-free and works anywhere the required props are available.
 */
export const MediaDeleteModalHost = ({ openRef }: MediaDeleteModalHostProps) => {
  const libraryFolder = useLibraryFolder();
  const library = useLibrary();
  const uploadManager = useUploadManager();

  const [currentMedia, setCurrentMedia] = useState<Media | Media[] | null>(null);
  const [currentOnDeleted, setCurrentOnDeleted] = useState<(() => void) | null>(null);
  const modalRef = useRef<MediaDeleteModalRef | null>(null);

  useEffect(() => {
    openRef.current = (media: Media | Media[], onDeleted?: () => void) => {
      setCurrentMedia(media);
      setCurrentOnDeleted(() => onDeleted ?? null);
    };
    return () => {
      openRef.current = null;
    };
  }, [openRef]);

  useEffect(() => {
    if (currentMedia && modalRef.current) {
      modalRef.current.open();
    }
  }, [currentMedia]);

  const handleConfirmDelete = useCallback(
    async (mediaIds: string[]) => {
      if (mediaIds.length > 1) {
        await libraryFolder.deleteBulkMedia(mediaIds);
      } else if (mediaIds.length === 1) {
        await libraryFolder.deleteMedia(mediaIds[0]);
      }
      if (libraryFolder.library?.libraryId) {
        uploadManager.refreshLibrary(libraryFolder.library.libraryId);
      }
    },
    [libraryFolder, uploadManager],
  );

  if (!currentMedia) return null;

  return (
    <MediaDeleteModal
      ref={modalRef}
      items={mediaToDeleteItems(currentMedia)}
      libraryId={library.libraryId}
      libraryName={library.data?.name || 'Library'}
      onConfirmDelete={handleConfirmDelete}
      onClose={() => {
        setCurrentMedia(null);
        setCurrentOnDeleted(null);
      }}
      onDeleted={currentOnDeleted ?? undefined}
    />
  );
};
